import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  aiConditionToHumanReadable,
  HumanReadableTranslations,
} from '@polybank/shared';
import type { AICondition } from '@polybank/shared/types/ai-contracts';
import useCategories from '@/hooks/use-categories';
import { useBankAccounts } from '@/hooks/use-bank-accounts';

/**
 * Hook that provides a function to generate human-readable rule descriptions
 * using client-side translation and name resolution.
 *
 * This replaces the AI-based regenerateHumanReadable API call with instant,
 * deterministic, and properly i18n-supported generation.
 */
export function useHumanReadableGenerator() {
  const { t } = useTranslation();
  const { categoriesMap } = useCategories();
  const { bankAccountsMap } = useBankAccounts();

  // Build translations object from i18n
  const translations: HumanReadableTranslations = useMemo(
    () => ({
      everyTime: t('humanReadable.everyTime'),
      then: t('humanReadable.then'),
      and: t('humanReadable.and'),
      or: t('humanReadable.or'),
      conditions: {
        descriptionContainsExactly: t(
          'humanReadable.conditions.descriptionContainsExactly'
        ),
        descriptionContainsInOrder: t(
          'humanReadable.conditions.descriptionContainsInOrder'
        ),
        descriptionContainsAny: t(
          'humanReadable.conditions.descriptionContainsAny'
        ),
        descriptionContains: t('humanReadable.conditions.descriptionContains'),
        descriptionDoesNotContain: t(
          'humanReadable.conditions.descriptionDoesNotContain'
        ),
        descriptionStartsWith: t(
          'humanReadable.conditions.descriptionStartsWith'
        ),
        descriptionEndsWith: t('humanReadable.conditions.descriptionEndsWith'),
        amountGreaterThan: t('humanReadable.conditions.amountGreaterThan'),
        amountGreaterOrEqual: t(
          'humanReadable.conditions.amountGreaterOrEqual'
        ),
        amountLessThan: t('humanReadable.conditions.amountLessThan'),
        amountLessOrEqual: t('humanReadable.conditions.amountLessOrEqual'),
        amountEquals: t('humanReadable.conditions.amountEquals'),
        amountBetween: t('humanReadable.conditions.amountBetween'),
        currencyIs: t('humanReadable.conditions.currencyIs'),
        accountIs: t('humanReadable.conditions.accountIs'),
        dateStartingFrom: t('humanReadable.conditions.dateStartingFrom'),
        dateUntil: t('humanReadable.conditions.dateUntil'),
        dateBetween: t('humanReadable.conditions.dateBetween'),
        dateOn: t('humanReadable.conditions.dateOn'),
      },
      actions: {
        setCategory: t('humanReadable.actions.setCategory'),
        addTag: t('humanReadable.actions.addTag'),
        addNote: t('humanReadable.actions.addNote'),
        setAccount: t('humanReadable.actions.setAccount'),
        setMerchant: t('humanReadable.actions.setMerchant'),
      },
    }),
    [t]
  );

  // Category name resolver: converts category ID to display name
  const categoryNameResolver = useCallback(
    (categoryId: string): string | undefined => {
      return categoriesMap[categoryId]?.name;
    },
    [categoriesMap]
  );

  // Account name resolver: converts account ID to display name
  const accountNameResolver = useCallback(
    (accountId: string): string | undefined => {
      return bankAccountsMap[accountId]?.account_name;
    },
    [bankAccountsMap]
  );

  /**
   * Generate a human-readable description from an AICondition object.
   *
   * @param condition - The AICondition to convert
   * @returns A human-readable string with formatting markers (**condition** and ##action##)
   */
  const generate = useCallback(
    (condition: AICondition): string => {
      return aiConditionToHumanReadable(condition, {
        translations,
        categoryNameResolver,
        accountNameResolver,
      });
    },
    [translations, categoryNameResolver, accountNameResolver]
  );

  return { generate };
}
